import { useMemo } from "react";
import { Checkbox, Skeleton } from "@vibe/core";
import { ColumnValueType, Item } from "../../../../services/monday/monday-query-interface";
import { DropdownChevronLeft, DropdownChevronRight } from "@vibe/icons";
import { cn } from "@utils/tailwind-merge";
import { PersonColumn } from "@components/ItemViewSection/columns";
import ButtonComponent from "@components/Button";
import { useSyncWizardStep1ViewModel } from "./SyncWizardContext";

const STATUS_FALLBACK_COLOR = "#c4c4c4";
const STATUS_HEADER_FALLBACK = "Status";
const PERSON_HEADER_FALLBACK = "Person";

const pickFirstStatus = (cvs: ColumnValueType[] = []): ColumnValueType | undefined =>
  cvs.find((cv) => cv.type === "status" || cv.label !== undefined);

const pickFirstPerson = (cvs: ColumnValueType[] = []): ColumnValueType | undefined =>
  cvs.find((cv) => cv.type === "people" || (cv.persons_and_teams?.length ?? 0) > 0);

const findFirstColumnTitle = (
  items: Item[],
  picker: (cvs: ColumnValueType[]) => ColumnValueType | undefined,
): string | undefined => {
  for (const item of items) {
    const cv = picker(item.column_values ?? []);
    const title = cv?.column?.title;
    if (title) return title;
  }
  return undefined;
};

const itemIdString = (id: Item["id"]): string => String(id);

const SKELETON_ROW_COUNT = 6;

const ItemRowSkeleton = () => (
  <div className="flex items-center gap-3 px-3 py-2 border-b border-t-0 border-solid border-x-0 border-[#f0f0f0] last:border-0">
    <div className="flex-1">
      <Skeleton type="text" width={180} height={16} />
    </div>
    <div className="flex justify-center w-28">
      <Skeleton type="rectangle" width={72} height={16} />
    </div>
    <div className="flex justify-center w-8">
      <Skeleton type="circle" size="small" height={20} width={20} />
    </div>
  </div>
);

export function SyncWizardStep1() {
  const {
    currentBoardName,
    items,
    selected,
    setSelected,
    searchTerm,
    onSearchTermChange,
    isLoading,
    currentPage,
    pageCount,
    hasMore,
    onPrev,
    onNext,
    hasError,
    onRetry,
  } = useSyncWizardStep1ViewModel();

  const visibleIds = items.map((item) => itemIdString(item.id));
  const allOnPageSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
  const someOnPageSelected = visibleIds.some((id) => selected.includes(id));

  const toggle = (id: string) => {
    setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  const toggleAllOnPage = () => {
    if (allOnPageSelected) {
      setSelected(selected.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelected(Array.from(new Set([...selected, ...visibleIds])));
    }
  };

  const canGoPrev = currentPage > 0;
  const canGoNext = hasMore || currentPage + 1 < pageCount;
  const displayPage = currentPage + 1;
  const showEmptyState = !isLoading && items.length === 0;

  const statusHeader = useMemo(
    () => findFirstColumnTitle(items, pickFirstStatus) ?? STATUS_HEADER_FALLBACK,
    [items],
  );
  const personHeader = useMemo(
    () => findFirstColumnTitle(items, pickFirstPerson) ?? PERSON_HEADER_FALLBACK,
    [items],
  );

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <p className="text-[12px] text-[#676879] !mb-2 flex-shrink-0">
        Choose which items from{" "}
        <span className="text-[#323338] font-semibold">{currentBoardName}</span> you want to share.
      </p>

      <div className="!mb-2 flex-shrink-0">
        <input
          type="text"
          value={searchTerm}
          onChange={(event) => onSearchTermChange(event.target.value)}
          placeholder="Search items by name"
          className="w-full h-8 px-3 text-[12px] text-[#323338] rounded-md border border-solid border-[#c3c6d4] focus:outline-none focus:border-[#0073ea]"
        />
      </div>

      <div className="flex items-center gap-3 px-3 py-2 border-b border-solid border-[#e6e9ef] bg-[#f6f7fb] rounded-t-lg flex-shrink-0">
        <Checkbox
          checked={allOnPageSelected}
          indeterminate={someOnPageSelected && !allOnPageSelected}
          onChange={toggleAllOnPage}
          disabled={items.length === 0}
        />
        <span className="text-[11px] font-semibold text-[#676879] flex-1">Item</span>
        <span
          className="text-[11px] font-semibold text-[#676879] w-28 text-center truncate"
          title={statusHeader}
        >
          {statusHeader}
        </span>
        <span
          className="text-[11px] font-semibold text-[#676879] w-28 text-center truncate"
          title={personHeader}
        >
          {personHeader}
        </span>
      </div>

      <div className="border-t-0 border-solid border-[#e6e9ef] rounded-b-lg overflow-y-auto min-h-0">
        {hasError && (
          <div className="px-3 py-6">
            <div className="flex flex-col gap-2 items-center text-center">
              <p className="text-[12px] text-[#676879] !mb-0">
                We couldn&apos;t load items from this board.
              </p>
              <ButtonComponent text="Retry" className="retry-button" onClick={onRetry} />
            </div>
          </div>
        )}

        {!hasError &&
          isLoading &&
          items.length === 0 &&
          Array.from({ length: SKELETON_ROW_COUNT }).map((_, idx) => (
            <ItemRowSkeleton key={`skeleton-${idx}`} />
          ))}

        {!hasError && showEmptyState && (
          <div className="px-3 py-4 text-[12px] text-[#676879]">
            {searchTerm.trim() ? "No items match this search." : "No items on this board."}
          </div>
        )}

        {!hasError &&
          items.map((item) => {
          const id = itemIdString(item.id);
          const isSelected = selected.includes(id);
          const status = pickFirstStatus(item.column_values);
          const person = pickFirstPerson(item.column_values);

          return (
            <div
              key={id}
              className="flex items-center gap-3 px-3 py-2 hover:bg-[#f6f7fb] cursor-pointer transition-colors border-b border-t-0 border-solid border-x-0 border-[#f0f0f0] last:border-0"
              onClick={() => toggle(id)}
              style={{ backgroundColor: isSelected ? "#f0f7ff" : undefined }}
            >
              <span onClick={(e) => e.stopPropagation()}>
                <Checkbox checked={isSelected} onChange={() => toggle(id)} />
              </span>
              <span className="flex-1 text-[12px] text-[#323338] truncate">{item.name}</span>

              <div className="w-28 text-center">
                {status?.label ? (
                  <span
                    className="text-xs text-white font-semibold rounded-full w-full px-2 py-0.5 inline-block truncate max-w-full"
                    style={{ backgroundColor: status.label_style?.color ?? STATUS_FALLBACK_COLOR }}
                    title={status.label}
                  >
                    {status.label}
                  </span>
                ) : (
                  <span className="text-[10px] text-[#c4c4c4]">—</span>
                )}
              </div>

              <div className="flex justify-center w-28">
                {person?.text ? (
                  <PersonColumn columnData={person as any} itemIds={[]} isPreview={true} />
                ) : (
                  <span className="text-[10px] text-[#c4c4c4]">—</span>
                )}
              </div>
            </div>
          );
          })}
      </div>

      <div className="flex items-center justify-between !mt-2 flex-shrink-0">
        <div>
          {selected.length > 0 && (
            <p className="text-[11px] text-[#0073ea] font-semibold">
              {selected.length} item{selected.length > 1 ? "s" : ""} selected
            </p>
          )}
        </div>
        <div className="flex gap-2 justify-center items-center">
          <div
            role="button"
            aria-disabled={!canGoPrev}
            onClick={canGoPrev ? onPrev : undefined}
            className={cn(
              "flex justify-center items-center",
              canGoPrev ? "cursor-pointer" : "opacity-50 cursor-not-allowed",
            )}
          >
            <DropdownChevronLeft color="#676879" />
          </div>
          <span className="text-xs font-semibold text-[#676879]">{displayPage}</span>
          <div
            role="button"
            aria-disabled={!canGoNext || isLoading}
            onClick={canGoNext && !isLoading ? onNext : undefined}
            className={cn(
              "flex items-center justify-center",
              canGoNext && !isLoading ? "cursor-pointer" : "opacity-50 cursor-not-allowed",
            )}
          >
            <DropdownChevronRight color="#676879" />
          </div>
        </div>
      </div>
    </div>
  );
}
